/**
 * Piping NDE (Non-Destructive Examination) Discipline Configuration
 * 
 * Configuration for the NDE Tracker sub-view of the piping dashboard.
 * Columns based on the NDE tracking requirements:
 * - Welder #, NDE Type, ISO Sheet #, Line Number, Fluid Code
 * - Qty Joint, Qty NDE, Qty Failed
 */

export const FIELD_MAP = {
  date: 'date',
  welderNumber: 'welder_number',
  ndeType: 'nde_type',
  isoSheetNumber: 'iso_sheet_number',
  lineNumber: 'line_number',
  fluidCode: 'fluid_code',
  qtyJoint: 'qty_joint',
  qtyNde: 'qty_nde',
  qtyFailed: 'qty_failed',
  comments: 'comments',
};

export const EDITABLE_COLUMNS = [
  'date', 'welderNumber', 'ndeType', 'isoSheetNumber', 'lineNumber',
  'fluidCode', 'qtyJoint', 'qtyNde', 'qtyFailed', 'comments',
];

export const transformMatrixData = (matrixData) => {
  return matrixData.map((row, index) => ({
    id: row.id || index,
    uuid: row.uuid || '',
    date: row.date || '',
    modifiedBy: row.modified_by || '',
    welderNumber: row.welder_number || '',
    ndeType: row.nde_type || '',
    isoSheetNumber: row.iso_sheet_number || '',
    lineNumber: row.line_number || '',
    fluidCode: row.fluid_code || '',
    qtyJoint: row.qty_joint != null ? row.qty_joint : '',
    qtyNde: row.qty_nde != null ? row.qty_nde : '',
    qtyFailed: row.qty_failed != null ? row.qty_failed : '',
    comments: row.comments || '',
  }));
};

export const buildChangePayload = (rowId, columnId, oldValue, newValue) => {
  const backendField = FIELD_MAP[columnId];
  if (!backendField) return null;

  return {
    rowId,
    field: backendField,
    value: newValue,
  };
};

export const pipingNdeConfig = {
  id: 'piping-nde',
  name: 'NDE Tracker',
  menuId: 'piping-nde',
  
  api: {
    getData: '/nde/project/{projectId}',
    saveChanges: '/nde/save-changes',
    export: '/nde/export/{projectId}',
    template: '/nde/template',
    analytics: '/nde/analytics/{projectId}',
    availableIsos: '/nde/available-isos/{projectId}',
    importFromTracker: '/nde/import-from-tracker',
    importExcel: '/nde/import-excel',
    addRecord: '/nde/add-record',
    deleteRecords: '/nde/records',
    duplicateRecords: '/nde/duplicate-records',
    auditLog: '/nde-audit-log/project/{projectId}',
    requiredPercentages: '/nde/required-percentages/{projectId}',
  },
  
  permissions: {
    edit: 'pt_edit',
    editAll: 'pt_edit_all',
    analytics: 'pt_analytics',
    import: 'pt_import',
    export: 'pt_export',
    columnConfig: 'pt_columnconfig',
    delete: 'pt_delete',
  },
  
  columns: [
    {
      id: 'date',
      header: 'Date',
      accessor: 'date',
      dbField: 'date',
      width: 140,
      editable: true,
      filterable: true,
      type: 'date',
      showTimestamp: true,
    },
    {
      id: 'modifiedBy',
      header: 'Modified By',
      accessor: 'modifiedBy',
      dbField: 'modified_by',
      width: 150,
      editable: false,
      filterable: true,
      type: 'text',
    },
    {
      id: 'welderNumber',
      header: 'Welder #',
      accessor: 'welderNumber',
      dbField: 'welder_number',
      width: 120,
      editable: true,
      filterable: true,
      type: 'select',
      options: [],
      dynamicOptionsUrl: '/welder-log/all',
      dynamicOptionsKey: 'welder_number',
      headerColor: '#E3F2FD',
      headerGroup: 'QC Manual',
    },
    {
      id: 'ndeType',
      header: 'NDE Type',
      accessor: 'ndeType',
      dbField: 'nde_type',
      width: 100,
      editable: true,
      filterable: true,
      type: 'select',
      options: ['', 'X-Ray', 'LPI', 'MPI', 'UT', 'VT', 'PMI'],
      headerColor: '#E3F2FD',
      headerGroup: 'QC Manual',
    },
    {
      id: 'isoSheetNumber',
      header: 'ISO Sheet #',
      accessor: 'isoSheetNumber',
      dbField: 'iso_sheet_number',
      width: 160,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#E8F5E9',
      headerGroup: 'From Tracker',
    },
    {
      id: 'lineNumber',
      header: 'Line Number',
      accessor: 'lineNumber',
      dbField: 'line_number',
      width: 140,
      editable: true,
      filterable: true,
      type: 'text',
      headerColor: '#E8F5E9',
      headerGroup: 'From Tracker',
    },
    {
      id: 'fluidCode',
      header: 'Fluid Code',
      accessor: 'fluidCode',
      dbField: 'fluid_code',
      width: 100,
      editable: false,
      filterable: true,
      type: 'text',
      headerColor: '#E8F5E9',
      headerGroup: 'From Tracker',
    },
    {
      id: 'qtyJoint',
      header: 'Qty Joint',
      accessor: 'qtyJoint',
      dbField: 'qty_joint',
      width: 90,
      editable: true,
      filterable: true,
      type: 'number',
      headerColor: '#E3F2FD',
      headerGroup: 'QC Manual',
    },
    {
      id: 'qtyNde',
      header: 'Qty NDE',
      accessor: 'qtyNde',
      dbField: 'qty_nde',
      width: 90,
      editable: true,
      filterable: true,
      type: 'number',
      headerColor: '#E3F2FD',
      headerGroup: 'QC Manual',
    },
    {
      id: 'qtyFailed',
      header: 'Qty Failed',
      accessor: 'qtyFailed',
      dbField: 'qty_failed',
      width: 90,
      editable: true,
      filterable: true,
      type: 'number',
      headerColor: '#FFEBEE',
      headerGroup: 'QC Manual',
    },
    {
      id: 'comments',
      header: 'Comments',
      accessor: 'comments',
      dbField: 'comments',
      width: 200,
      editable: true,
      filterable: true,
      type: 'text',
    },
  ],
  
  hiddenColumns: [],
  
  features: {
    dragFill: true,
    progressTrends: false,
    rulesOfCredit: false,
    shopFieldRatio: false,
    matBore: false,
    fabricationShops: false,
    priorities: false,
    columnMappings: false,
    udfColumns: false,
    presence: true, // Real-time presence tracking (shows who's viewing)
  },
  
  transformData: transformMatrixData,
  buildChangePayload,
  FIELD_MAP,
  EDITABLE_COLUMNS,
};
